import { Datum } from "../../../components/docs/Datum.js";
import { note, p, table, ul } from "../blocks.js";
import type { WikiTopic } from "../types.js";

export const familyAndHorizon: WikiTopic = {
  slug: "family-and-horizon",
  title: "Family and horizon",
  summary:
    "Family names the job that wrote the row. Horizon names how long the trade means to last.",
  group: "concepts",
  sections: [
    {
      heading: "Family names the emitter",
      code: "rank / technical / event / intraday / swing",
      blocks: [
        p(
          <>
            Every row carries a <Datum>family</Datum>, and it answers one question: which job
            emitted this. It has never answered how long the setup means to be held, and reading it
            that way is the mistake this page exists to prevent.
          </>,
        ),
        ul([
          <>
            <Datum>rank</Datum> and <Datum>technical</Datum> come from the daily job at the session
            close.
          </>,
          <>
            <Datum>intraday</Datum> comes from the hourly runner, including the confluence rows it
            synthesises from the votes.
          </>,
          <>
            <Datum>swing</Datum> comes from the ICT job that runs after the daily chain.
          </>,
          <>
            <Datum>event</Datum> comes from the same evening job, and today it carries one type: the
            ex-date avoid flag on a bonus or split.
          </>,
        ]),
      ],
    },
    {
      heading: "Horizon names the hold",
      code: "session / swing / position",
      blocks: [
        p(
          <>
            <Datum>horizon</Datum> is the second label, added because three values are what the
            strategies actually do.
          </>,
        ),
        table(
          "The three trade horizons",
          ["Horizon", "What it means", "Who has it"],
          [
            [
              <Datum key="a">session</Datum>,
              "Flat by the close",
              "Every hourly strategy and the confluence they vote into",
            ],
            [
              <Datum key="b">swing</Datum>,
              "Days to a few weeks",
              "Prophet swing and the ICT setup",
            ],
            [
              <Datum key="c">position</Datum>,
              "The daily book",
              "Factor rank crossings, the 52-week extremes, and the ex-date avoid flag",
            ],
          ],
        ),
        note(
          <>
            A <Datum>position</Datum> row is a reason to look at a name, not a trade that closes on
            Friday. A factor decile crossing says the ranking moved, and nothing more.
          </>,
        ),
      ],
    },
    {
      heading: "Prophet swing is the case that proves it",
      code: "prophet_swing_entry / prophet_swing_exit",
      blocks: [
        p(
          <>
            Prophet swing writes <Datum>family: "intraday"</Datum> because the hourly runner is what
            evaluates it. It holds for days.
          </>,
        ),
        p(
          <>
            So a feed sorted by time puts a gap fade that is over by <Datum>15:30</Datum> one row
            above a swing long opened four sessions ago, both labelled by the job that wrote them.
            Read as holding periods, the two look like one strategy contradicting itself.
          </>,
        ),
        p(
          <>
            The ICT setup runs the other way round: <Datum>family: "swing"</Datum> from the
            session-close job, and a <Datum>swing</Datum> horizon to match. Family and horizon agree
            there by coincidence, not by rule.
          </>,
        ),
      ],
    },
    {
      heading: "Why the event filter came late",
      blocks: [
        p(
          <>
            For months the schema allowed <Datum>family: "event"</Datum> and nothing wrote it. The
            announcement classifier fills the <Datum>events</Datum> table and stops there, so the
            filter left the button out: one that could only ever report zero looks the same as a
            rank job that died.
          </>,
        ),
        p(
          <>
            The button came back with its first emitter, the ex-date avoid flag. Filtering to{" "}
            <Datum>event</Datum> shows every bonus and split still inside its window.
          </>,
        ),
        note(
          <>
            This wiki's rail groups strategies by horizon rather than by family, for the reason
            above: horizon is the label a reader deciding what to do with a row actually needs.
          </>,
        ),
      ],
    },
  ],
};
